import {
    createContext,
    useContext,
    useMemo,
    useRef,
    useState,
    type Dispatch,
    type ReactNode,
    type SetStateAction,
} from "react";

import { applyFormDraftToBuilder, formDraftToBuilderData } from "@/features/ai-builder/formRevision";
import type { FormDraft } from "@/features/ai-builder/formDraft";
import type { FormBuilderData } from "@/features/forms/api/formBuilderApi";

interface FormBuilderWorkspaceValue {
    transient: true;
    form: FormBuilderData;
    getForm: () => FormBuilderData;
    setForm: Dispatch<SetStateAction<FormBuilderData>>;
    applyDraft: (draft: FormDraft) => void;
}

const FormBuilderWorkspaceContext = createContext<FormBuilderWorkspaceValue | null>(null);

export function FormBuilderWorkspaceProvider({
    initialDraft,
    children,
}: {
    initialDraft: FormDraft;
    children: ReactNode;
}) {
    const [form, setFormState] = useState(() => formDraftToBuilderData(initialDraft));
    const formRef = useRef(form);

    const setForm: Dispatch<SetStateAction<FormBuilderData>> = (update) => {
        const next = typeof update === "function" ? update(formRef.current) : update;
        formRef.current = next;
        setFormState(next);
    };

    const value = useMemo(
        () => ({
            transient: true as const,
            form,
            getForm: () => formRef.current,
            setForm,
            applyDraft: (draft: FormDraft) => setForm((current) => applyFormDraftToBuilder(current, draft)),
        }),
        [form]
    );

    return <FormBuilderWorkspaceContext.Provider value={value}>{children}</FormBuilderWorkspaceContext.Provider>;
}

export function useFormBuilderWorkspace() {
    return useContext(FormBuilderWorkspaceContext);
}

export function asLocalMutation<TArg, TResult>(run: (arg: TArg) => TResult) {
    return {
        isPending: false,
        reset: () => undefined,
        mutate: (arg: TArg, options?: { onSuccess?: (data: TResult) => void; onError?: (error: unknown) => void }) => {
            try {
                const data = run(arg);
                options?.onSuccess?.(data);
            } catch (error) {
                options?.onError?.(error);
            }
        },
        mutateAsync: async (arg: TArg) => run(arg),
    };
}
